import { datadogRum } from '@datadog/browser-rum';
import { ClaimJourneyEntryPoint } from '@whitelabel/xcover-shared/helpers/types';

// A support agent filing a claim on the customer's behalf lands here from XTranet with
// `agent_token` in the URL. It is kept for the tab's lifetime.
// The token is forwarded to the claims portal backend as X-Agent-Token on claim create, where
// XClaim uses it for attribution.
export const AGENT_TOKEN = 'agentToken';
// Tags every RUM + BW event of a session with the actor context.
export const RUM_CLAIMS_ACTOR = 'claims_actor';
const AGENT_SESSION_EXPIRED = 'agentSessionExpired';
const AGENT_LAUNCH_PENDING = 'agentLaunchPending';
export const AGENT_SESSION_CHANGE_EVENT = 'agent-session-change';

export type ClaimsActorRole = 'support_agent_internal';

export interface ClaimsActor {
  role: ClaimsActorRole;
  okta_sub_id: string;
}

const readSession = (key: string): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sessionStorage.getItem(key) ?? undefined;
};

// Decodes the token payload for display and housekeeping only. The signature is not checked here;
// XClaim verifies the token on claim create.
const readTokenClaims = (token?: string): Record<string, unknown> | undefined => {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return undefined;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

export const getAgentToken = (): string | undefined => readSession(AGENT_TOKEN);

/**
 * Inline `<head>` script that sets `window.isCookieConsentExempt` for support agent sessions, so CookieYes is skipped
 * and analytics load straight away. Consent is not asked of internal staff. It must run before the CookieYes tag is
 * written, so it can't wait for the app bundle. Checks the URL for the first landing from XTranet (before
 * captureAgentToken() strips it) and sessionStorage for reloads. Never clears an exemption already set.
 */
export const buildAgentConsentExemptionScript = (): string => `
      (function () {
        try {
          if (
            new URLSearchParams(window.location.search).has('agent_token') ||
            sessionStorage.getItem(${JSON.stringify(AGENT_TOKEN)})
          ) {
            window.isCookieConsentExempt = true;
          }
        } catch (e) {}
      })();`;

// The claims actor context for the current agent session: { role: 'support_agent_internal',
// okta_sub_id: <okta_sub> }. Old tokens minted before the `okta_sub` claim was embedded return undefined.
export const getClaimsActor = (): ClaimsActor | undefined => {
  // On initial launch (?agent_token=...), _app.tsx runs useBWTag() (firing the first 'pv' event)
  // before ProtectedLayout mounts and moves agent_token into sessionStorage. Fall back to the URL
  // param so the initial 'pv' already has the actor context.
  const token =
    getAgentToken() ??
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('agent_token') ?? undefined
      : undefined);
  const oktaSub = readTokenClaims(token)?.okta_sub;
  if (typeof oktaSub !== 'string') return undefined;
  return { role: 'support_agent_internal', okta_sub_id: oktaSub };
};

let activeActor: ClaimsActor | undefined;
let bwtagBase: ((...args: unknown[]) => unknown) | undefined;

// bw-analytics pushes auto page views to bwDataLayer as [{ appId, method: 'pv' }],
// whereas manual page views pass 'pv'.
const isPv = (cmd: unknown): boolean => cmd === 'pv' || (cmd as { method?: string })?.method === 'pv';

// Injects claims_actor into Brightwrite's custom_variables payload argument:
// - 'record' events: ['record', eventName, payload, ...rest] -> payload is args[2]
// - 'pv' events:     [{ appId, method: 'pv' }, payload]      -> payload is args[1]
const withActor = (args: unknown[]): unknown[] => {
  if (!activeActor) return args;
  const enrich = (payload: unknown) => ({
    ...(payload as object),
    entryPoint: (payload as { entryPoint?: string })?.entryPoint ?? ClaimJourneyEntryPoint.XtranetManualClaim,
    [RUM_CLAIMS_ACTOR]: activeActor,
  });
  if (args[0] === 'record') {
    return [args[0], args[1], enrich(args[2]), ...args.slice(3)];
  }
  if (isPv(args[0])) {
    return [args[0], enrich(args[1]), ...args.slice(2)];
  }
  return args;
};

// Sets the claims_actor session variable on both RUM and Brightwrite for an active support agent session.
// Note: bwtag('set', 'claims_actor', ...) is ignored by bw-analytics (setProperties only accepts hardcoded
// SDK config keys). Instead, we intercept 'record' calls on window.bwtag and internal 'pv' pushes on
// window.bwDataLayer.push (via an accessor so it survives the SDK reassigning bwDataLayer.push on load).
export const setClaimsActor = (actor: ClaimsActor) => {
  activeActor = actor;
  datadogRum.setGlobalContextProperty(RUM_CLAIMS_ACTOR, actor);
  if (typeof window === 'undefined') return;
  if (typeof window.bwtag === 'function') {
    const base = bwtagBase ?? (window.bwtag as NonNullable<typeof bwtagBase>);
    bwtagBase = base;
    window.bwtag = (...args: unknown[]) => base(...withActor(args));
  }
  const layer = (window as { bwDataLayer?: unknown[] }).bwDataLayer;
  if (Array.isArray(layer) && !Object.getOwnPropertyDescriptor(layer, 'push')?.get) {
    let rawPush = layer.push.bind(layer);
    Object.defineProperty(layer, 'push', {
      configurable: true,
      get: () => (entry: unknown) =>
        rawPush(activeActor && Array.isArray(entry) && isPv(entry[0]) ? withActor(entry) : entry),
      set: (fn: typeof rawPush) => {
        rawPush = fn;
      },
    });
  }
};

export const clearClaimsActor = () => {
  activeActor = undefined;
  datadogRum.removeGlobalContextProperty(RUM_CLAIMS_ACTOR);
  if (bwtagBase) window.bwtag = bwtagBase;
  bwtagBase = undefined;
};

export const setAgentToken = (token: string) => {
  sessionStorage.setItem(AGENT_TOKEN, token);
  const claimsActor = getClaimsActor();
  if (claimsActor) setClaimsActor(claimsActor);
};

export const clearAgentToken = ({ keepActor = false } = {}) => {
  [AGENT_TOKEN, AGENT_SESSION_EXPIRED, AGENT_LAUNCH_PENDING].forEach((key) => sessionStorage.removeItem(key));
  if (!keepActor) clearClaimsActor();
};

// Stores the launch URL's agent_token and returns the query without it, or undefined when there was no
// agent token.
export const captureAgentToken = <T extends Record<string, unknown>>(query: T): Omit<T, 'agent_token'> | undefined => {
  const { agent_token: agentToken, ...rest } = query;
  if (typeof agentToken !== 'string' || !agentToken) return undefined;
  // A new launch starts a new session, even in a tab whose previous session expired.
  clearAgentToken();
  setAgentToken(agentToken);
  sessionStorage.setItem(AGENT_LAUNCH_PENDING, 'true');
  window.dispatchEvent(new Event(AGENT_SESSION_CHANGE_EVENT));
  return rest;
};

// True once per launch from XTranet, so StartAClaim can take the agent straight to the claim selector.
export const consumeAgentLaunch = (): boolean => {
  const isPending = !!readSession(AGENT_LAUNCH_PENDING);
  if (isPending) sessionStorage.removeItem(AGENT_LAUNCH_PENDING);
  return isPending;
};

export const getAgentTokenExpiry = (token?: string): number | undefined => {
  const exp = readTokenClaims(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : undefined;
};

// The token is minted for one booking. Drops the session when this tab moves on to another booking,
// so the banner, the CP backend forcing and X-Agent-Token don't carry over to it.
export const clearAgentSessionForOtherBooking = (bookingID?: string) => {
  const bookingReference = readTokenClaims(getAgentToken())?.booking_reference;
  if (!bookingID || typeof bookingReference !== 'string') return;
  if (bookingReference.toUpperCase() === bookingID.toUpperCase()) return;
  clearAgentToken();
  window.dispatchEvent(new Event(AGENT_SESSION_CHANGE_EVENT));
};

export const isAgentSessionExpired = (now = Date.now()): boolean => {
  if (readSession(AGENT_SESSION_EXPIRED)) return true;
  const expiresAt = getAgentTokenExpiry(getAgentToken());
  return expiresAt !== undefined && now >= expiresAt;
};

// Ends the agent session early, e.g. when XClaim rejects the token before the countdown reaches zero.
export const expireAgentSession = () => {
  sessionStorage.setItem(AGENT_SESSION_EXPIRED, 'true');
  window.dispatchEvent(new Event(AGENT_SESSION_CHANGE_EVENT));
};

// XClaim rejects an invalid, expired or wrong-booking agent token with a 422 on `agentToken`.
export const isAgentTokenError = (error: unknown): boolean => {
  const errors = (error as { data?: { errors?: Record<string, unknown> } })?.data?.errors;
  return !!errors && ('agentToken' in errors || 'agent_token' in errors);
};

export const getXTranetBookingURL = (bookingID?: string): string | undefined => {
  const baseURL = process.env.NEXT_PUBLIC_XTRANET_URL;
  return baseURL && bookingID ? `${baseURL}/bookings/${encodeURIComponent(bookingID)}` : undefined;
};

export const getRCAdminBookingURL = (bookingID?: string): string | undefined => {
  const baseURL = process.env.VITE_RC_ADMIN_URL;
  return baseURL && bookingID
    ? `${baseURL}/booking/results?searchOption=ref&search=${encodeURIComponent(bookingID)}`
    : undefined;
};
